/**
 * QboImportDialog — reprise des soldes depuis QuickBooks : date, fermeture des revenus/dépenses dans les bénéfices non répartis,
 * aperçu de la balance QBO avec correspondance par compte (compte local existant · nouveau compte n°/nom · ignorer), import → écriture d'ouverture.
 */
import { useMemo, useState } from "react";
import { Download, Eye } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { AccountSelect, Btn, Modal, errMsg, inputCls, money } from "../../comptabilite/_shared";

function lastDayPrevYear() { return `${new Date().getFullYear() - 1}-12-31`; }

function Row({ r, accounts, onChange, testId }) {
  const mode = r.skip ? "skip" : r.account_id ? "map" : "create";
  const setMode = (m) => onChange({ ...r, skip: m === "skip", account_id: m === "map" ? (r.account_id || "") : null, create: m === "create" ? (r.create || { number: "", name: r.name }) : r.create });
  return (
    <tr className={`${r.skip ? "opacity-50" : ""} ${r.closed_to_re ? "bg-amber-50/60" : ""}`} data-testid={testId}>
      <td className="px-2 py-1.5 align-top">
        <div className="font-medium text-slate-800 text-xs">{r.acct_num && <span className="font-mono text-slate-500 mr-1">{r.acct_num}</span>}{r.name}</div>
        <div className="text-[10px] text-slate-400">{r.classification}{r.sub_type ? ` · ${r.sub_type}` : ""}{r.closed_to_re ? " · fermé dans les BNR" : ""}</div>
      </td>
      <td className="px-2 py-1.5 text-right font-mono text-xs tabular-nums align-top">{r.debit ? money(r.debit) : ""}</td>
      <td className="px-2 py-1.5 text-right font-mono text-xs tabular-nums align-top">{r.credit ? money(r.credit) : ""}</td>
      <td className="px-2 py-1.5 align-top">
        <select value={mode} onChange={(e) => setMode(e.target.value)} className={`${inputCls} text-xs py-1`} data-testid={`${testId}-mode`}>
          <option value="map">Compte existant</option><option value="create">Nouveau compte</option><option value="skip">Ignorer</option>
        </select>
      </td>
      <td className="px-2 py-1.5 align-top min-w-[220px]">
        {mode === "map" && <AccountSelect accounts={accounts} value={r.account_id || ""} onChange={(v) => onChange({ ...r, account_id: v })} types={[r.type]} testId={`${testId}-account`} />}
        {mode === "create" && (
          <div className="flex gap-1">
            <input value={r.create?.number || ""} onChange={(e) => onChange({ ...r, create: { ...(r.create || {}), number: e.target.value.replace(/\D/g, "").slice(0, 4) } })} className={`${inputCls} w-16 font-mono text-xs py-1`} placeholder="N°" data-testid={`${testId}-number`} />
            <input value={r.create?.name || ""} onChange={(e) => onChange({ ...r, create: { ...(r.create || {}), name: e.target.value } })} className={`${inputCls} text-xs py-1`} placeholder="Nom du compte" maxLength={150} data-testid={`${testId}-name`} />
          </div>
        )}
        {mode === "skip" && <span className="text-[11px] text-slate-400">Solde non repris</span>}
        {mode !== "skip" && r.how && <div className="text-[10px] text-slate-400 mt-0.5">{r.how}</div>}
      </td>
    </tr>
  );
}

export default function QboImportDialog({ masterId, accounts, settings, onClose, onDone }) {
  const [asOf, setAsOf] = useState(settings?.opening_date && settings.opening_date !== `${new Date().getFullYear()}-01-01` ? settings.opening_date : lastDayPrevYear());
  const [closePl, setClosePl] = useState(true);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);

  const loadPreview = async () => {
    setBusy("preview"); setResult(null);
    try {
      const r = await api.post(`/qbo/connections/${masterId}/preview`, { as_of: asOf, close_pl: closePl });
      setPreview(r.data); setRows(r.data.rows.map((x) => ({ ...x, skip: false })));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };
  const doImport = async () => {
    const bad = rows.find((r) => !r.skip && !r.account_id && !(r.create?.number?.length === 4 && r.create?.name?.trim()));
    if (bad) { toast.error(`Compte à préciser : ${bad.name}`); return; }
    if (!window.confirm(`Reporter l'écriture d'ouverture au ${asOf} à partir de QuickBooks ? Elle remplace l'écriture d'ouverture existante du dossier.`)) return;
    setBusy("import");
    try {
      const r = await api.post(`/qbo/connections/${masterId}/import`, { as_of: asOf, close_pl: closePl, rows: rows.map(({ qbo_id, name, type, debit, credit, balance, account_id, create, skip }) => ({ qbo_id, name, type, debit, credit, balance, account_id: account_id || null, create: create && !account_id ? create : null, skip })) });
      setResult(r.data); toast.success(`Soldes repris — ${r.data.accounts} compte(s), ${r.data.created} créé(s)`); onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };
  const update = (i, v) => setRows((p) => p.map((r, k) => (k === i ? v : r)));
  const counts = useMemo(() => ({ map: rows.filter((r) => !r.skip && r.account_id).length, create: rows.filter((r) => !r.skip && !r.account_id).length, skip: rows.filter((r) => r.skip).length }), [rows]);

  return (
    <Modal title="Reprendre les soldes depuis QuickBooks" sub="Balance de vérification QuickBooks à la date choisie → correspondance avec le plan comptable du dossier → écriture d'ouverture (contrepartie « Solde d'ouverture »)." onClose={onClose} wide testId="qbo-import-dialog"
      footer={<>
        <Btn tone="slate" onClick={onClose} testId="qbo-import-close">Fermer</Btn>
        {preview && !result && <Btn icon={Download} onClick={doImport} busy={busy === "import"} testId="qbo-import-confirm">Importer ({rows.length - counts.skip} compte(s))</Btn>}
      </>}>
      <div className="flex items-end gap-3 flex-wrap">
        <label className="text-xs text-slate-600">Soldes au
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className={`${inputCls} block mt-1`} data-testid="qbo-as-of" />
        </label>
        <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer pb-2">
          <input type="checkbox" checked={closePl} onChange={(e) => setClosePl(e.target.checked)} className="accent-emerald-600" data-testid="qbo-close-pl" />
          Fermer revenus et dépenses dans les bénéfices non répartis (fin d'exercice)
        </label>
        <Btn tone="sky" icon={Eye} onClick={loadPreview} busy={busy === "preview"} testId="qbo-preview-btn">Aperçu</Btn>
      </div>
      {preview && (
        <>
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-600" data-testid="qbo-preview-summary">
            <span className="px-2 py-1 rounded-lg bg-white border">{preview.company_name || "QuickBooks"} · {preview.totals.count} compte(s)</span>
            <span className="px-2 py-1 rounded-lg bg-white border font-mono">Débits {money(preview.totals.debit)} · Crédits {money(preview.totals.credit)}</span>
            <span className="px-2 py-1 rounded-lg bg-emerald-50 border border-emerald-200">{counts.map} associé(s)</span>
            <span className="px-2 py-1 rounded-lg bg-sky-50 border border-sky-200">{counts.create} à créer</span>
            {counts.skip > 0 && <span className="px-2 py-1 rounded-lg bg-slate-100 border">{counts.skip} ignoré(s)</span>}
            {preview.close_pl && <span className="px-2 py-1 rounded-lg bg-amber-50 border border-amber-200">Résultat net {money(preview.pl_net)} → {preview.retained_earnings ? `${preview.retained_earnings.number} ${preview.retained_earnings.name}` : "BNR (rôle à définir !)"}</span>}
            {preview.locked_until && preview.locked_until >= asOf && <span className="px-2 py-1 rounded-lg bg-rose-50 border border-rose-200 text-rose-700">Période verrouillée jusqu'au {preview.locked_until}</span>}
          </div>
          <div className="border rounded-xl overflow-auto max-h-[50vh]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0"><tr><th className="text-left px-2 py-2">Compte QuickBooks</th><th className="text-right px-2 py-2 w-28">Débit</th><th className="text-right px-2 py-2 w-28">Crédit</th><th className="text-left px-2 py-2 w-36">Action</th><th className="text-left px-2 py-2">Compte du dossier</th></tr></thead>
              <tbody className="divide-y">{rows.map((r, i) => <Row key={r.qbo_id} r={r} accounts={accounts} onChange={(v) => update(i, v)} testId={`qbo-row-${i}`} />)}</tbody>
            </table>
          </div>
        </>
      )}
      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900 space-y-1" data-testid="qbo-import-result">
          <div className="font-bold">Écriture d'ouverture reportée au {result.as_of}</div>
          <div>{result.accounts} compte(s) avec solde · {result.created} compte(s) créé(s) · {result.skipped} ignoré(s){result.close_pl ? ` · résultat net fermé : ${money(result.pl_closed)}` : ""} · écart à la contrepartie : {money(result.difference_to_equity)}</div>
          {result.created_accounts?.length > 0 && <div className="text-[11px]">Créés : {result.created_accounts.map((c) => `${c.number} ${c.name}`).join(", ")}</div>}
        </div>
      )}
    </Modal>
  );
}
